"use client";

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ArrowLeft, Save, Loader2, AlertCircle, MessageSquare } from "lucide-react";
import MisstatementFormRenderer from "./MisstatementFormRenderer";
import {
  getMisstatementTemplate,
  MisstatementTemplate,
} from "../../templates/misstatement-templates";
import { Dialog, DialogContent } from "../ui/dialog";
import { usePlanningMateriality } from "../../hooks/usePlanningMateriality";
import { useArchivalMode } from "../../contexts/ArchivalModeContext";
import { ReviewComment } from "../shared/ReviewComment";

interface MisstatementDetailViewProps {
  misstatementId: string;
  misstatementData: any;
  workspaceName: string;
  onBack: () => void;
  onSave?: (data: any) => Promise<void>;
  mode?: "view" | "edit";
  onMisstatementClick?: (misstatementId: string) => void;
  onDeficiencyClick?: (deficiencyId: string) => void;
}

export default function MisstatementDetailView({
  misstatementId,
  misstatementData,
  workspaceName,
  onBack,
  onSave,
  mode = "view",
  onMisstatementClick,
  onDeficiencyClick,
}: MisstatementDetailViewProps) {
  const { isArchivalMode } = useArchivalMode();
  const [isEditMode, setIsEditMode] = useState(mode === "edit");
  
  // Prevent edit mode in archival mode
  useEffect(() => {
    if (isArchivalMode && isEditMode) {
      setIsEditMode(false);
    }
  }, [isArchivalMode, isEditMode]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");
  const { materialityData, loading: loadingMateriality } = usePlanningMateriality();
  const [allMisstatements, setAllMisstatements] = useState<Array<{ id: string; label: string }>>([]);
  const [controlDeficiencies, setControlDeficiencies] = useState<Array<{ id: string; label: string }>>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [selectedMisstatement, setSelectedMisstatement] = useState<{ id: string; data: any } | null>(null);
  const [selectedDeficiency, setSelectedDeficiency] = useState<{ id: string; data: any } | null>(null);
  const [fsLineItems, setFsLineItems] = useState<Array<{ id: string; label: string }>>([]);
  const [rembManagementEstimates, setRembManagementEstimates] = useState<Array<{ id: string; label: string }>>([]);
  const [activeReviewComment, setActiveReviewComment] = useState<{
    questionId: string;
    questionText: string;
    workspace?: string;
    subPhase?: string;
  } | null>(null);

  // Load management estimates from REMB-1 table in ConcludingProcedures_TailoringQuestions_Responses.json
  useEffect(() => {
    const loadRembManagementEstimates = async () => {
      try {
        if (window.sharePointAPI && typeof window.sharePointAPI.downloadClientJson === "function") {
          const result = await window.sharePointAPI.downloadClientJson("ConcludingProcedures_TailoringQuestions_Responses.json");
          
          if (result?.success && result?.data) {
            const responsesData = result.data;
            // Navigate to "Review of estimates for management bias" workspace -> "REMB-1" question
            const workspaceName = "Review of estimates for management bias";
            const remb1Data = responsesData[workspaceName]?.["REMB-1"];
            
            if (remb1Data?.table && Array.isArray(remb1Data.table)) {
              // Extract unique values from col_0 (Name of Estimate)
              const estimateSet = new Set<string>();
              remb1Data.table.forEach((row: any) => {
                const estimate = row.col_0;
                if (estimate && typeof estimate === "string" && estimate.trim() !== "") {
                  estimateSet.add(estimate.trim());
                }
              });
              
              // Convert Set to array of objects with id and label
              const estimateItems = Array.from(estimateSet)
                .sort()
                .map((estimate) => ({
                  id: estimate,
                  label: estimate,
                }));
              
              setRembManagementEstimates(estimateItems);
            } else {
              setRembManagementEstimates([]);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load REMB management estimates from ConcludingProcedures_TailoringQuestions_Responses.json:", error);
        setRembManagementEstimates([]);
      }
    };
    
    loadRembManagementEstimates();
  }, []);

  // Load unique note_line values from FinData_TrialBalance_Mapping.json
  useEffect(() => {
    const loadNoteLineOptions = async () => {
      try {
        if (window.sharePointAPI && typeof window.sharePointAPI.downloadTrialBalanceMapping === "function") {
          const result = await window.sharePointAPI.downloadTrialBalanceMapping({ includeAmounts: true });
          
          if (result?.success && result?.data) {
            const mappingData = result.data;
            const noteLineSet = new Set<string>();
            
            // Extract unique note_line values from all books
            for (const [bookName, entries] of Object.entries(mappingData)) {
              if (Array.isArray(entries)) {
                entries.forEach((entry: any) => {
                  const noteLine = entry.note_line || entry.noteLine;
                  if (noteLine && typeof noteLine === "string" && noteLine.trim() !== "") {
                    noteLineSet.add(noteLine.trim());
                  }
                });
              }
            }
            
            // Convert Set to array of objects with id and label
            const noteLineItems = Array.from(noteLineSet)
              .sort()
              .map((noteLine) => ({
                id: noteLine,
                label: noteLine,
              }));
            
            setFsLineItems(noteLineItems);
          }
        }
      } catch (error) {
        console.error("Failed to load note_line options from FinData_TrialBalance_Mapping.json:", error);
      }
    };
    
    loadNoteLineOptions();
  }, []);

  const misstatementType =
    misstatementData?.type || "Factual";
  const template = getMisstatementTemplate(
    misstatementType as "Factual" | "Judgemental" | "Projected",
  );

  // Mapping function to convert template field IDs to JSON structure keys
  const getJsonKeyFromFieldId = (fieldId: string, templateType: string): string | null => {
    if (fieldId.startsWith(`${templateType}_q_`)) {
      const questionKey = fieldId.replace(`${templateType}_q_`, "");
      
      // Map template question IDs to JSON structure keys
      const questionMapping: Record<string, string> = {
        communicated: "communicated_to_TCWG",
        management_response: "communicated_to_TCWG", // This is nested under communicated_to_TCWG
        profit_impact: "affect_profits",
        offset: "linked_misst",
        offset_list: "linked_misst", // This maps to linked_misst.misstatement array
        legal_violation: "compliance",
        fraud: "fraud",
        control_deficiency: "control_deficiency",
        deficiency_list: "control_deficiency", // This maps to control_deficiency.deficiency array
        prior_period: "misstatement_linkage",
        prior_list: "misstatement_linkage", // This maps to misstatement_linkage.misstatement array
        material_conclusion: "conclusion",
        classification_specific: "classification",
        disclosure_specific: "disclosure",
      };
      
      return questionMapping[questionKey] || null;
    }
    return null;
  };

  // Initialize form data from misstatement data
  useEffect(() => {
    if (misstatementData) {
      const initialData: Record<string, any> = {};

      // Extract data based on type
      const typeData =
        misstatementData.factual ||
        misstatementData.judgemental ||
        misstatementData.projected ||
        {};

      const templateType = template.type; // "factual", "judgmental", or "projected"

      // Load misstatement value (from Materiality Summary)
      if (typeData.misstatement !== undefined) {
        if (templateType === "factual") {
          initialData["factual_misstatement"] = typeData.misstatement || "";
        } else if (templateType === "judgmental") {
          initialData["judgmental_misstatement"] = typeData.misstatement || "";
        } else if (templateType === "projected") {
          initialData["projected_misstatement"] = typeData.misstatement || "";
        }
      } else if (templateType === "projected") {
        // Calculate projected misstatement from calculation table if not set
        const A = parseFloat(typeData.calculation?.rows?.[0]?.population || "0");
        const B = parseFloat(typeData.calculation?.rows?.[0]?.misstatement || "0");
        const C = parseFloat(typeData.calculation?.rows?.[1]?.population || "0");
        if (A > 0 && B > 0 && C > 0) {
          initialData["projected_misstatement"] = (C * B) / A;
        }
      }

      // Map template fields to form data
      template.fields.forEach((field) => {
        // Handle table field
        if (field.type === "table" && field.id === "factual_table") {
          initialData["factual_amount"] = typeData.table?.row?.amount || "";
          initialData["factual_description"] = typeData.table?.row?.description || "";
          initialData["factual_abcotd"] = typeData.table?.row?.abcotd || [];
          initialData["factual_period"] = typeData.table?.row?.period || "";
          initialData["factual_type"] = typeData.table?.row?.type || "";
        } else if (field.type === "table" && field.id === "judgmental_table") {
          initialData["judgmental_auditor_value"] = typeData.table?.row?.amount || "";
          initialData["judgmental_description"] = typeData.table?.row?.description || "";
          initialData["judgmental_management_estimate"] = typeData.table?.row?.estimate || "";
          initialData["judgmental_abcotd"] = typeData.table?.row?.abcotd || [];
          initialData["judgmental_period"] = typeData.table?.row?.period || "";
          initialData["judgmental_type"] = typeData.table?.row?.type || "";
        }
        // Handle nested structures
        else if (field.id.startsWith("factual_")) {
          const key = field.id.replace("factual_", "");
          if (key === "amount") {
            initialData[field.id] = typeData.table?.row?.amount || "";
          } else if (key === "description") {
            initialData[field.id] = typeData.table?.row?.description || "";
          } else if (key === "abcotd") {
            initialData[field.id] = typeData.table?.row?.abcotd || [];
          } else if (key === "period") {
            initialData[field.id] = typeData.table?.row?.period || "";
          } else if (key === "type") {
            initialData[field.id] = typeData.table?.row?.type || "";
          } else if (key.startsWith("q_")) {
            const questionKey = key.replace("q_", "");
            const jsonKey = getJsonKeyFromFieldId(field.id, "factual");
            
            if (jsonKey && typeData[jsonKey]) {
              const questionData = typeData[jsonKey];
              
              // Handle different question types
              if (questionKey === "communicated") {
                initialData[field.id] = questionData.selectedOption || "";
                initialData[`${field.id}_textResponse`] = questionData.textResponse || null;
                initialData[`${field.id}_documents`] = questionData.documents || [];
                
                // Handle management_response nested structure
                if (questionData.management_response) {
                  initialData[`factual_q_management_response_textResponse`] =
                    questionData.management_response.textResponse || null;
                  initialData[`factual_q_management_response_documents`] =
                    questionData.management_response.documents || [];
                }
              } else if (questionKey === "offset_list") {
                // This maps to linked_misst.misstatement array
                initialData[field.id] = questionData.misstatement || [];
              } else if (questionKey === "deficiency_list") {
                // This maps to control_deficiency.deficiency array
                initialData[field.id] = questionData.deficiency || [];
              } else if (questionKey === "prior_list") {
                // This maps to misstatement_linkage.misstatement array
                initialData[field.id] = questionData.misstatement || [];
              } else {
                // Standard question structure
                initialData[field.id] = questionData.selectedOption || "";
                initialData[`${field.id}_textResponse`] = questionData.textResponse || null;
                initialData[`${field.id}_documents`] = questionData.documents || [];
              }
            }
          }
        } else if (field.id.startsWith("judgmental_")) {
          const key = field.id.replace("judgmental_", "");
          // Skip table fields as they're handled above
          if (key === "table") {
            // Already handled above
          } else if (key.startsWith("q_")) {
            const questionKey = key.replace("q_", "");
            const jsonKey = getJsonKeyFromFieldId(field.id, "judgmental");
            
            if (jsonKey && typeData[jsonKey]) {
              const questionData = typeData[jsonKey];
              
              if (questionKey === "communicated") {
                initialData[field.id] = questionData.selectedOption || "";
                initialData[`${field.id}_textResponse`] = questionData.textResponse || null;
                initialData[`${field.id}_documents`] = questionData.documents || [];
                
                if (questionData.management_response) {
                  initialData[`judgmental_q_management_response_textResponse`] =
                    questionData.management_response.textResponse || null;
                  initialData[`judgmental_q_management_response_documents`] =
                    questionData.management_response.documents || [];
                }
              } else if (questionKey === "offset_list") {
                initialData[field.id] = questionData.misstatement || [];
              } else if (questionKey === "deficiency_list") {
                initialData[field.id] = questionData.deficiency || [];
              } else if (questionKey === "prior_list") {
                initialData[field.id] = questionData.misstatement || [];
              } else {
                initialData[field.id] = questionData.selectedOption || "";
                initialData[`${field.id}_textResponse`] = questionData.textResponse || null;
                initialData[`${field.id}_documents`] = questionData.documents || [];
              }
            }
          }
        } else if (field.type === "table" && field.id === "projected_table") {
          initialData["projected_description"] = typeData.table?.row?.description || "";
          initialData["projected_abcotd"] = typeData.table?.row?.abcotd || [];
          initialData["projected_period"] = typeData.table?.row?.period || "";
          initialData["projected_type"] = typeData.table?.row?.type || "Other";
        } else if (field.type === "table" && field.id === "projected_calculation_table") {
          initialData["projected_particulars_row1"] = typeData.calculation?.rows?.[0]?.particulars || "";
          initialData["projected_population_row1"] = typeData.calculation?.rows?.[0]?.population || "";
          initialData["projected_misstatement_row1"] = typeData.calculation?.rows?.[0]?.misstatement || "";
          initialData["projected_population_row2"] = typeData.calculation?.rows?.[1]?.population || "";
        } else if (field.id.startsWith("projected_")) {
          const key = field.id.replace("projected_", "");
          if (key.startsWith("q_")) {
            const questionKey = key.replace("q_", "");
            const jsonKey = getJsonKeyFromFieldId(field.id, "projected");
            
            if (jsonKey && typeData[jsonKey]) {
              const questionData = typeData[jsonKey];
              
              if (questionKey === "communicated") {
                initialData[field.id] = questionData.selectedOption || "";
                initialData[`${field.id}_textResponse`] = questionData.textResponse || null;
                initialData[`${field.id}_documents`] = questionData.documents || [];
                
                if (questionData.management_response) {
                  initialData[`projected_q_management_response_textResponse`] =
                    questionData.management_response.textResponse || null;
                  initialData[`projected_q_management_response_documents`] =
                    questionData.management_response.documents || [];
                }
              } else if (questionKey === "offset_list") {
                initialData[field.id] = questionData.misstatement || [];
              } else if (questionKey === "deficiency_list") {
                initialData[field.id] = questionData.deficiency || [];
              } else if (questionKey === "prior_list") {
                initialData[field.id] = questionData.misstatement || [];
              } else {
                initialData[field.id] = questionData.selectedOption || "";
                initialData[`${field.id}_textResponse`] = questionData.textResponse || null;
                initialData[`${field.id}_documents`] = questionData.documents || [];
              }
            }
          }
        }
      });

      setFormData(initialData);
    }
  }, [misstatementData, template]);

  // Load misstatements and deficiencies from SharePoint
  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      try {
        // Load misstatements
        if (window.sharePointAPI && typeof window.sharePointAPI.downloadMisstatementJson === "function") {
          const misRes = await window.sharePointAPI.downloadMisstatementJson();
          if (misRes?.success && misRes.data) {
            const misstatementData = misRes.data;
            const librariesMisstatement =
              misstatementData.Libraries_Misstatement ||
              misstatementData.libraries_Misstatement ||
              {};
            const misstatementsObj = librariesMisstatement.misstatements || {};
            
            const items = Object.entries(misstatementsObj).map(([id, value]: [string, any]) => ({
              id,
              label: `${id} - ${value.type || "Factual"}`,
            }));
            setAllMisstatements(items);
          }
        }

        // Load deficiencies
        if (window.sharePointAPI && typeof window.sharePointAPI.downloadDeficiencyJson === "function") {
          const defRes = await window.sharePointAPI.downloadDeficiencyJson();
          if (defRes?.success && defRes.data) {
            const rawDef = defRes.data.libraries_deficiency || {};
            const items = Object.entries(rawDef).map(([id, value]: [string, any]) => ({
              id,
              label: `${id} - ${value.deficiency_type || "Deficiency"}`,
            }));
            setControlDeficiencies(items);
          }
        }
      } catch (error) {
        console.error("Failed to load misstatements or deficiencies:", error);
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, []);

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleDocumentChange = (fieldId: string, documents: any[]) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: documents,
    }));
  };

  const handleTextResponseChange = (
    fieldId: string,
    textResponse: string | null,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: textResponse,
    }));
  };

  const handleSave = async () => {
    if (!onSave) return;

    try {
      setSaving(true);
      setError("");

      // Transform form data back to misstatement structure
      const updatedData = transformFormDataToMisstatement(
        formData,
        template,
        misstatementType,
      );

      await onSave(updatedData);
      setIsEditMode(false);
    } catch (err: any) {
      setError(err?.message || "Failed to save misstatement");
    } finally {
      setSaving(false);
    }
  };

  // Transform form data back to the misstatement JSON structure
  const transformFormDataToMisstatement = (
    data: Record<string, any>,
    template: MisstatementTemplate,
    type: string,
  ): any => {
    const result: any = {
      workspace: workspaceName,
      type: type,
    };

    if (type === "Factual") {
      result.factual = {
        table: {
          columns: [
            { key: "amount", label: "Amount", type: "number" },
            { key: "description", label: "Description", type: "text" },
            { key: "abcotd", label: "ABCOTD", type: "text" },
            { key: "period", label: "Period", type: "text" },
            { key: "type", label: "Type", type: "text" },
          ],
          row: {
            amount: data.factual_amount || "",
            description: data.factual_description || null,
            abcotd: data.factual_abcotd || [],
            period: data.factual_period || "",
            type: data.factual_type || "",
          },
        },
        misstatement: data.factual_misstatement || data.factual_amount || "",
        communicated_to_TCWG: {
          selectedOption: data.factual_q_communicated || "",
          textResponse: data.factual_q_management_response_textResponse || null,
          documents: data.factual_q_communicated_documents || [],
          management_response: {
            textResponse: data.factual_q_management_response_textResponse || null,
            documents: data.factual_q_management_response_documents || [],
          },
        },
        affect_profits: {
          selectedOption: data.factual_q_profit_impact || "",
          textResponse: data.factual_q_profit_impact_textResponse || null,
          documents: data.factual_q_profit_impact_documents || [],
        },
        linked_misst: {
          selectedOption: data.factual_q_offset || "",
          misstatement: data.factual_q_offset_list || [],
        },
        compliance: {
          selectedOption: data.factual_q_legal_violation || "",
          textResponse: data.factual_q_legal_violation_textResponse || null,
          documents: data.factual_q_legal_violation_documents || [],
        },
        fraud: {
          selectedOption: data.factual_q_fraud || "",
          textResponse: data.factual_q_fraud_textResponse || null,
          documents: data.factual_q_fraud_documents || [],
        },
        control_deficiency: {
          selectedOption: data.factual_q_control_deficiency || "",
          deficiency: data.factual_q_deficiency_list || [],
        },
        misstatement_linkage: {
          selectedOption: data.factual_q_prior_period || "",
          misstatement: data.factual_q_prior_list || [],
        },
        conclusion: {
          selectedOption: data.factual_q_material_conclusion || "",
          textResponse: data.factual_q_material_conclusion_textResponse || null,
          documents: data.factual_q_material_conclusion_documents || [],
        },
        classification: {
          selectedOption: data.factual_q_classification_specific || "",
          textResponse: data.factual_q_classification_specific_textResponse || null,
          documents: data.factual_q_classification_specific_documents || [],
        },
        disclosure: {
          selectedOption: data.factual_q_disclosure_specific || "",
          textResponse: data.factual_q_disclosure_specific_textResponse || null,
          documents: data.factual_q_disclosure_specific_documents || [],
        },
      };
    } else if (type === "Judgemental") {
      result.judgemental = {
        table: {
          columns: [
            { key: "amount", label: "Amount", type: "number" },
            { key: "description", label: "Description", type: "text" },
            { key: "estimate", label: "Management Estimate", type: "text" },
            { key: "abcotd", label: "ABCOTD", type: "text" },
            { key: "period", label: "Period", type: "text" },
            { key: "type", label: "Type", type: "text" },
          ],
          row: {
            amount: data.judgmental_auditor_value || "",
            description: data.judgmental_description || null,
            estimate: data.judgmental_management_estimate || "",
            abcotd: data.judgmental_abcotd || [],
            period: data.judgmental_period || "",
            type: data.judgmental_type || "",
          },
        },
        misstatement: data.judgmental_misstatement || "",
        communicated_to_TCWG: {
          selectedOption: data.judgmental_q_communicated || "",
          textResponse: data.judgmental_q_management_response_textResponse || null,
          documents: data.judgmental_q_communicated_documents || [],
          management_response: {
            textResponse: data.judgmental_q_management_response_textResponse || null,
            documents: data.judgmental_q_management_response_documents || [],
          },
        },
        affect_profits: {
          selectedOption: data.judgmental_q_profit_impact || "",
          textResponse: data.judgmental_q_profit_impact_textResponse || null,
          documents: data.judgmental_q_profit_impact_documents || [],
        },
        linked_misst: {
          selectedOption: data.judgmental_q_offset || "",
          misstatement: data.judgmental_q_offset_list || [],
        },
        compliance: {
          selectedOption: data.judgmental_q_legal_violation || "",
          textResponse: data.judgmental_q_legal_violation_textResponse || null,
          documents: data.judgmental_q_legal_violation_documents || [],
        },
        fraud: {
          selectedOption: data.judgmental_q_fraud || "",
          textResponse: data.judgmental_q_fraud_textResponse || null,
          documents: data.judgmental_q_fraud_documents || [],
        },
        control_deficiency: {
          selectedOption: data.judgmental_q_control_deficiency || "",
          deficiency: data.judgmental_q_deficiency_list || [],
        },
        misstatement_linkage: {
          selectedOption: data.judgmental_q_prior_period || "",
          misstatement: data.judgmental_q_prior_list || [],
        },
        conclusion: {
          selectedOption: data.judgmental_q_material_conclusion || "",
          textResponse: data.judgmental_q_material_conclusion_textResponse || null,
          documents: data.judgmental_q_material_conclusion_documents || [],
        },
        classification: {
          selectedOption: data.judgmental_q_classification_specific || "",
          textResponse: data.judgmental_q_classification_specific_textResponse || null,
          documents: data.judgmental_q_classification_specific_documents || [],
        },
        disclosure: {
          selectedOption: data.judgmental_q_disclosure_specific || "",
          textResponse: data.judgmental_q_disclosure_specific_textResponse || null,
          documents: data.judgmental_q_disclosure_specific_documents || [],
        },
      };
    } else if (type === "Projected") {
      result.projected = {
        table: {
          columns: [
            { key: "description", label: "Description", type: "text" },
            { key: "abcotd", label: "ABCOTD", type: "text" },
            { key: "period", label: "Period", type: "text" },
            { key: "type", label: "Type", type: "text" },
          ],
          row: {
            description: data.projected_description || null,
            abcotd: data.projected_abcotd || [],
            period: data.projected_period || "",
            type: data.projected_type || "Other",
          },
        },
        calculation: {
          columns: [
            { key: "particulars", label: "Particulars", type: "text" },
            { key: "population", label: "Population", type: "number" },
            { key: "misstatement", label: "Misstatement", type: "number" },
          ],
          rows: [
            {
              particulars: data.projected_particulars_row1 || "",
              population: data.projected_population_row1 || "",
              misstatement: data.projected_misstatement_row1 || "",
            },
            {
              particulars: "Total Population",
              population: data.projected_population_row2 || "",
              misstatement: (() => {
                // Calculate: (C * B) / A
                const A = parseFloat(data.projected_population_row1 || "0");
                const B = parseFloat(data.projected_misstatement_row1 || "0");
                const C = parseFloat(data.projected_population_row2 || "0");
                if (A > 0 && B > 0 && C > 0) {
                  return (C * B) / A;
                }
                return "";
              })(),
            },
          ],
        },
        misstatement: data.projected_misstatement || (() => {
          // Calculate from calculation table
          const A = parseFloat(data.projected_population_row1 || "0");
          const B = parseFloat(data.projected_misstatement_row1 || "0");
          const C = parseFloat(data.projected_population_row2 || "0");
          if (A > 0 && B > 0 && C > 0) {
            return (C * B) / A;
          }
          return "";
        })(),
        communicated_to_TCWG: {
          selectedOption: data.projected_q_communicated || "",
          textResponse: data.projected_q_management_response_textResponse || null,
          documents: data.projected_q_communicated_documents || [],
          management_response: {
            textResponse: data.projected_q_management_response_textResponse || null,
            documents: data.projected_q_management_response_documents || [],
          },
        },
        affect_profits: {
          selectedOption: data.projected_q_profit_impact || "",
          textResponse: data.projected_q_profit_impact_textResponse || null,
          documents: data.projected_q_profit_impact_documents || [],
        },
        linked_misst: {
          selectedOption: data.projected_q_offset || "",
          misstatement: data.projected_q_offset_list || [],
        },
        compliance: {
          selectedOption: data.projected_q_legal_violation || "",
          textResponse: data.projected_q_legal_violation_textResponse || null,
          documents: data.projected_q_legal_violation_documents || [],
        },
        fraud: {
          selectedOption: data.projected_q_fraud || "",
          textResponse: data.projected_q_fraud_textResponse || null,
          documents: data.projected_q_fraud_documents || [],
        },
        control_deficiency: {
          selectedOption: data.projected_q_control_deficiency || "",
          deficiency: data.projected_q_deficiency_list || [],
        },
        misstatement_linkage: {
          selectedOption: data.projected_q_prior_period || "",
          misstatement: data.projected_q_prior_list || [],
        },
        conclusion: {
          selectedOption: data.projected_q_material_conclusion || "",
          textResponse: data.projected_q_material_conclusion_textResponse || null,
          documents: data.projected_q_material_conclusion_documents || [],
        },
        classification: {
          selectedOption: data.projected_q_classification_specific || "",
          textResponse: data.projected_q_classification_specific_textResponse || null,
          documents: data.projected_q_classification_specific_documents || [],
        },
        disclosure: {
          selectedOption: data.projected_q_disclosure_specific || "",
          textResponse: data.projected_q_disclosure_specific_textResponse || null,
          documents: data.projected_q_disclosure_specific_documents || [],
        },
      };
    }

    return result;
  };

  return (
    <div className="flex h-full flex-col gap-6 text-white">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={onBack}
            className="border-white/10 text-white hover:bg-white/10"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold">
              {misstatementId} - {misstatementType} Misstatement
            </h2>
            <p className="text-sm text-white/60">Workspace: {workspaceName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isEditMode && onSave && (
            <Button
              variant="outline"
              onClick={() => setIsEditMode(true)}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Edit
            </Button>
          )}
          {isEditMode && onSave && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditMode(false);
                  // Reset form data
                  setFormData({});
                }}
                disabled={saving}
                className="border-white/10 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || isArchivalMode}
                className="bg-blue-500 text-white hover:bg-blue-600"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border-red-500/40 bg-red-500/10">
          <CardContent className="flex items-center gap-2 p-4 text-sm text-red-200">
            <AlertCircle className="h-4 w-4" />
            {error}
          </CardContent>
        </Card>
      )}

      {/* Form Content */}
      <Card className="flex-1 border-white/10 bg-white/5">
        <CardContent className="p-6">
          <MisstatementFormRenderer
            template={template}
            formData={formData}
            onChange={handleFieldChange}
            onDocumentChange={handleDocumentChange}
            onTextResponseChange={handleTextResponseChange}
            disabled={!isEditMode || isArchivalMode}
            planningData={materialityData}
            misstatementId={misstatementId}
            fsLineItems={fsLineItems}
            rembManagementEstimates={rembManagementEstimates}
            allMisstatements={allMisstatements}
            controlDeficiencies={controlDeficiencies}
            onReviewCommentClick={(sectionId, sectionLabel) =>
              setActiveReviewComment({
                questionId: `misstatement-${misstatementId}-${template.type}-${sectionId}`,
                questionText: `${sectionLabel} - ${misstatementId} ${misstatementType} Misstatement`,
                workspace: workspaceName,
                subPhase: sectionLabel,
              })
            }
            onMisstatementClick={async (clickedMisstatementId: string) => {
              if (onMisstatementClick) {
                onMisstatementClick(clickedMisstatementId);
              } else {
                // Default behavior: load and show misstatement
                try {
                  if (window.sharePointAPI && typeof window.sharePointAPI.downloadMisstatementJson === "function") {
                    const misRes = await window.sharePointAPI.downloadMisstatementJson();
                    if (misRes?.success && misRes.data) {
                      const misstatementJsonData = misRes.data;
                      const librariesMisstatement =
                        misstatementJsonData.Libraries_Misstatement ||
                        misstatementJsonData.libraries_Misstatement ||
                        {};
                      const misstatementsObj = librariesMisstatement.misstatements || {};
                      const clickedMisstatementData = misstatementsObj[clickedMisstatementId];
                      if (clickedMisstatementData) {
                        setSelectedMisstatement({ id: clickedMisstatementId, data: clickedMisstatementData });
                      }
                    }
                  }
                } catch (error) {
                  console.error("Failed to load misstatement:", error);
                }
              }
            }}
            onDeficiencyClick={async (deficiencyId: string) => {
              if (onDeficiencyClick) {
                onDeficiencyClick(deficiencyId);
              } else {
                // Default behavior: load and show deficiency
                try {
                  if (window.sharePointAPI && typeof window.sharePointAPI.downloadDeficiencyJson === "function") {
                    const defRes = await window.sharePointAPI.downloadDeficiencyJson();
                    if (defRes?.success && defRes.data) {
                      const rawDef = defRes.data.libraries_deficiency || {};
                      const deficiencyData = rawDef[deficiencyId];
                      if (deficiencyData) {
                        setSelectedDeficiency({ id: deficiencyId, data: deficiencyData });
                      }
                    }
                  }
                } catch (error) {
                  console.error("Failed to load deficiency:", error);
                }
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Modal for viewing clicked misstatement */}
      {selectedMisstatement && (
        <Dialog open={!!selectedMisstatement} onOpenChange={() => setSelectedMisstatement(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <MisstatementDetailView
              misstatementId={selectedMisstatement.id}
              misstatementData={selectedMisstatement.data}
              workspaceName={selectedMisstatement.data.workspace || workspaceName}
              onBack={() => setSelectedMisstatement(null)}
              onSave={async (data) => {
                // Handle save if needed
                setSelectedMisstatement(null);
              }}
              mode="view"
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Modal for viewing clicked deficiency */}
      {selectedDeficiency && (
        <Dialog open={!!selectedDeficiency} onOpenChange={() => setSelectedDeficiency(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="space-y-4 text-white">
              <h2 className="text-2xl font-bold">{selectedDeficiency.id} - Deficiency</h2>
              <div className="space-y-2">
                <p><strong>Type:</strong> {selectedDeficiency.data.deficiency_type || "N/A"}</p>
                <p><strong>Control Relevance:</strong> {selectedDeficiency.data.control_relevance || "N/A"}</p>
                {selectedDeficiency.data.description && (
                  <div>
                    <strong>Description:</strong>
                    <div className="mt-1 p-2 bg-white/5 rounded">{selectedDeficiency.data.description}</div>
                  </div>
                )}
                {selectedDeficiency.data.root_cause && (
                  <div>
                    <strong>Root Cause:</strong>
                    <div className="mt-1 p-2 bg-white/5 rounded">{selectedDeficiency.data.root_cause}</div>
                  </div>
                )}
              </div>
              <Button onClick={() => setSelectedDeficiency(null)}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Review note modal */}
      {activeReviewComment && (
        <ReviewComment
          questionId={activeReviewComment.questionId}
          questionText={activeReviewComment.questionText}
          isOpen={true}
          onClose={() => setActiveReviewComment(null)}
          workspace={activeReviewComment.workspace}
          subPhase={activeReviewComment.subPhase}
        />
      )}
    </div>
  );
}

